import {vaultAssetKey} from './vaultAssetIdentity';
import { isAcceptedQuote, quoteItems, parseQuoteObject } from './quoteCalculations';
export interface VaultReference {kind:'shift'|'quote';id:string;url:string;}
export interface VaultMedia { assetKey?:string;hidden?:boolean;visibilityVersion?:number;references?:VaultReference[];url: string; shiftId?: string; quoteId?: string; section: string; date: string; isVideo: boolean; }
export interface VaultEvent { id: string; leadId?: string; name: string; client: string; date: string; media: VaultMedia[]; experiences: string[]; uncertain: boolean;marketingCategoryIds?:string[];marketingVersion?:number;categorySource?:'manual'|'quote'; }
export const mediaURLs = (value: any): string[] => {
  if (typeof value === 'string') { try { value = JSON.parse(value); } catch { return []; } }
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string' && !!v.trim()) : [];
};
export function buildVaultEvents(shifts: any[], quotes: any[], products: any[]): VaultEvent[] {
  const events = new Map<string, VaultEvent>();
  const add = (row: any, legacy: boolean) => {
    const urls = mediaURLs(legacy ? parseQuoteObject(row.onboarding_data).crewMedia : row.media_urls);
    if (!urls.length) return;
    const raw = row.event_leads;
    const lead = (Array.isArray(raw) ? raw[0] : raw) || {};
    const id = row.lead_id ? `lead:${row.lead_id}` : `${legacy ? 'quote' : 'shift'}:${row.id}`;
    let event = events.get(id);
    if (!event) {
      event = { id, leadId: row.lead_id || undefined, name: lead.project_ref || row.title || row.shift_title || 'Archived event',
        client: [lead.first_name, lead.last_name].filter(Boolean).join(' ') || row.client_company || '',
        date: row.event_date || '', media: [], experiences: [], uncertain: true };
      events.set(id, event);
    }
    if (row.event_date && (!event.date || row.event_date > event.date)) event.date = row.event_date;
    urls.forEach(url => {
      const assetKey=vaultAssetKey(url);
      const existing = event!.media.find(m => m.assetKey === assetKey);
      const reference:VaultReference={kind:legacy?'quote':'shift',id:row.id,url};
      if(existing){existing.references=[...(existing.references||[]),reference];return;}
      event!.media.push({ url, assetKey, references:[reference], shiftId: legacy ? undefined : row.id, quoteId: legacy ? row.id : undefined,
        section: legacy ? 'Legacy gallery' : row.shift_title || 'Event shift', date: row.event_date || '',
        isVideo: /\.(mp4|webm|mov)(?:[?#]|$)/i.test(url) });
    });
  };
  shifts.forEach(s => add(s, false)); quotes.forEach(q => add(q, true));
  for (const event of Array.from(events.values())) {
    const accepted = quotes.filter(q => isAcceptedQuote(q) && (event.leadId ? q.lead_id === event.leadId : event.id === `quote:${q.id}`));
    // Multiple accepted revisions are ambiguous; never silently choose one.
    if (accepted.length !== 1) continue;
    const items = quoteItems(accepted[0].items).filter(i => !i.isOptional && Number(i.quantity) > 0);
    let unresolved = !items.length || quoteItems(accepted[0].items).some(i => i.isOptional);
    const labels = new Set<string>();
    items.forEach((i: any) => {
      const product = products.find(p => String(p.id) === String(i.productId || i.product_id || ''));
      if (!product) { unresolved = true; return; }
      if (product.name) labels.add(product.name);
      else unresolved = true;
    });
    event.experiences = Array.from(labels).sort(); event.uncertain = unresolved;
  }
  return Array.from(events.values()).sort((a,b) => b.date.localeCompare(a.date) || a.name.localeCompare(b.name));
}

export async function readVaultRows(client: any) {
  const read = async (table: string, columns: string) => {
    const rows: any[] = [];
    for (let start = 0; ; start += 500) {
      const { data, error } = await client.from(table).select(columns).order('id').range(start, start + 499);
      if (error) throw new Error('Could not load event index');
      rows.push(...(data || []));
      if (!data || data.length < 500) return rows;
    }
  };
  const [shifts, quotes, products] = await Promise.all([
    read('event_shifts', 'id,shift_title,event_date,media_urls,lead_id,event_leads(project_ref,first_name,last_name)'),
    read('quotes', 'id,title,event_date,onboarding_data,lead_id,status,items,client_company'),
    read('products', 'id,name')
  ]);
  return buildVaultEvents(shifts, quotes, products);
}
